import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Gauge, Loader2 } from 'lucide-react';
import {
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    CartesianGrid,
} from 'recharts';
import { getMetaAppUsageHistory } from '@/services/api/connections';
import { MetaApp } from '../types/meta.types';

const USAGE_WARNING_THRESHOLD = 50;
const USAGE_CRITICAL_THRESHOLD = 75;

function getUsageColor(percent: number | null): string {
    if (percent === null) return 'bg-gray-200';
    if (percent >= USAGE_CRITICAL_THRESHOLD) return 'bg-red-500';
    if (percent >= USAGE_WARNING_THRESHOLD) return 'bg-yellow-400';
    return 'bg-green-500';
}

function getUsageTextColor(percent: number | null): string {
    if (percent === null) return 'text-gray-400';
    if (percent >= USAGE_CRITICAL_THRESHOLD) return 'text-red-600';
    if (percent >= USAGE_WARNING_THRESHOLD) return 'text-yellow-700';
    return 'text-green-600';
}

function formatTime(isoDate: string): string {
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return isoDate;
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

interface AppUsageModalProps {
    app: MetaApp | null;
    onOpenChange: (open: boolean) => void;
}

export default function AppUsageModal({ app, onOpenChange }: AppUsageModalProps) {
    const open = !!app;

    const { data: historyData, isLoading, isError } = useQuery({
        queryKey: ['meta-app-usage-history', app?.app_id],
        queryFn: () => getMetaAppUsageHistory(app!.app_id),
        enabled: open && !!app?.app_id,
    });

    const chartData = useMemo(() => {
        return (historyData?.history ?? []).map((point) => ({
            time: formatTime(point.recorded_at),
            usage: point.app_usage_percent,
        }));
    }, [historyData]);

    const latestUsage = chartData.length > 0 ? chartData[chartData.length - 1].usage : null;
    const atCeiling = (latestUsage ?? 0) >= USAGE_CRITICAL_THRESHOLD;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-xl font-bold text-[#0f172a] flex items-center gap-2">
                        <Gauge className="h-5 w-5 text-[#0f172a]" />
                        {app?.app_name}
                    </DialogTitle>
                    <DialogDescription className="font-mono text-xs">
                        App ID: {app?.app_id}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-700">Uso atual</span>
                        <div className="flex items-center gap-2">
                            {atCeiling && (
                                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[11px] gap-1">
                                    <AlertTriangle className="h-3 w-3" />
                                    Próximo do teto
                                </Badge>
                            )}
                            <span className={`text-sm font-semibold ${getUsageTextColor(latestUsage)}`}>
                                {latestUsage === null ? 'Sem dados' : `${latestUsage.toFixed(0)}%`}
                            </span>
                        </div>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all duration-300 ${getUsageColor(latestUsage)}`}
                            style={{ width: `${Math.min(Math.max(latestUsage ?? 0, 0), 100)}%` }}
                        />
                    </div>

                    <div className="h-56 w-full rounded-lg border border-gray-100 bg-gray-50/50 p-2">
                        {isLoading && (
                            <div className="flex h-full items-center justify-center text-gray-400 text-sm gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Carregando histórico...
                            </div>
                        )}
                        {!isLoading && isError && (
                            <div className="flex h-full items-center justify-center text-gray-400 text-sm text-center px-4">
                                Histórico indisponível no momento.
                            </div>
                        )}
                        {!isLoading && !isError && chartData.length === 0 && (
                            <div className="flex h-full items-center justify-center text-gray-400 text-sm">
                                Sem dados de histórico ainda.
                            </div>
                        )}
                        {!isLoading && !isError && chartData.length > 0 && (
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: -20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis dataKey="time" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                                    <YAxis
                                        tick={{ fontSize: 11 }}
                                        stroke="#9ca3af"
                                        domain={[0, 100]}
                                        unit="%"
                                    />
                                    <Tooltip
                                        formatter={(value?: number) => [`${(value ?? 0).toFixed(1)}%`, 'Uso do app']}
                                        labelFormatter={(label) => `Horário: ${label}`}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="usage"
                                        stroke="#0f172a"
                                        strokeWidth={2}
                                        dot={{ r: 2 }}
                                        connectNulls
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
